import { CustomDatePicker } from "@/components/form/CustomDatePicker";
import DatePicker from "@/components/form/DatePicker";
import Dropdown from "@/components/form/Dropdown";
import TextInput from "@/components/form/TextInput";
import { Button } from "@/components/ui/button";
import {
  CreateEmploymentInfo,
  EmploymentInfo,
  EmploymentStatus,
} from "@/models/employment-info.model";
import { ChevronLeft, ChevronRight } from "lucide-react";
import React, { useEffect } from "react";

export const employmentStatusOptions = Object.values(EmploymentStatus).map(
  (x) => ({
    label: x,
    value: x,
  })
);

export const passTypeOptions = [
  { label: "Dependent - Dp", value: "Dependent - Dp" },
  { label: "Employment Pass", value: "Employment Pass" },
  { label: "Malaysian - Wp", value: "Malaysian - Wp" },
  { label: "Others", value: "Others" },
  { label: "Prc - Wp", value: "Prc - Wp" },
  { label: "S Pass", value: "S Pass" },
  { label: "S Pass - Myan", value: "S Pass - Myan" },
  { label: "S Pass - Prc", value: "S Pass - Prc" },
  { label: "Singapore", value: "Singapore" },
  { label: "Singapore - Pr", value: "Singapore - Pr" },
  { label: "Special Pass", value: "Special Pass" },
  { label: "Training Employment Pass", value: "Training Employment Pass" },
  { label: "Work Permit", value: "Work Permit" },
];

export const employmentTypeOptions = [
  { label: "Full Time", value: "Full Time" },
  { label: "Part Time", value: "Part Time" },
  { label: "Contract Worker", value: "Contract Worker" },
  { label: "Training Staff", value: "Training Staff" },
  { label: "Temp Staff", value: "Temp Staff" },
  { label: "Probation Period", value: "Probation Period" },
];

interface Props {
  setPage: React.Dispatch<React.SetStateAction<number>>;
  employmentInfo: CreateEmploymentInfo | null;
  setEmploymentInfo: React.Dispatch<
    React.SetStateAction<CreateEmploymentInfo | null>
  >;
}
function EmploymentInfoForm({
  setPage,
  employmentInfo,
  setEmploymentInfo,
}: Props) {
  const [designation, setDesignation] = React.useState<string>(
    employmentInfo?.designation || ""
  );

  const [employmentStatus, setEmploymentStatus] = React.useState<string>(
    employmentInfo?.employmentStatus || EmploymentStatus.ACTIVE
  );
  const [hireDate, setHireDate] = React.useState<Date | undefined>(
    employmentInfo?.hireDate || new Date()
  );
  const [confirmedDate, setConfirmedDate] = React.useState<Date | undefined>(
    employmentInfo?.confirmedDate || new Date()
  );
  const [passType, setPassType] = React.useState<string>(
    employmentInfo?.passType || ""
  );
  const [employmentType, setEmploymentType] = React.useState<string>(
    employmentInfo?.employmentType || ""
  );
  const [err, setErr] = React.useState<string>("");

  const isValid = () => {
    if (
      designation.length === 0 ||
      passType.length === 0 ||
      employmentType.length === 0 ||
      !hireDate
    ) {
      setErr("Please fill in all compulsory fields.");
      return false;
    }
    setErr("");
    return true;
  };

  useEffect(() => {
    if (isValid()) {
      const employmentStatusVal = employmentStatus as EmploymentStatus;
      setEmploymentInfo({
        designation,
        employmentStatus: employmentStatusVal,
        hireDate: hireDate!,
        confirmedDate,
        passType,
        employmentType,
      });
    }
  }, [
    designation,
    employmentStatus,
    hireDate,
    confirmedDate,
    passType,
    employmentType,
  ]);

  const handleBack = () => {
    setPage((prev) => prev - 1);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid()) {
      return;
    }
    setPage((prev) => prev + 1);
    setErr("");
  };

  return (
    <form className="w-full h-full flex flex-col gap-4" onSubmit={handleSubmit}>
      <div className="flex w-full justify-between gap-2">
        <TextInput
          label="Designation *"
          value={designation}
          setValue={setDesignation}
          type="text"
        />
      </div>
      <div className="flex w-full justify-between gap-2">
        <CustomDatePicker
          date={hireDate}
          setDate={(date) => setHireDate(date)}
          label="Hire Date *"
        />
        <CustomDatePicker
          date={confirmedDate}
          setDate={setConfirmedDate}
          label="Confirmed Date"
        />
      </div>

      <Dropdown
        label="Employment Status"
        items={employmentStatusOptions}
        selectedString={employmentStatus}
        setSelectedString={setEmploymentStatus}
      />
      <Dropdown
        label="Pass Type *"
        items={passTypeOptions}
        selectedString={passType}
        setSelectedString={setPassType}
      />
      <Dropdown
        label="Employment Type *"
        items={employmentTypeOptions}
        selectedString={employmentType}
        setSelectedString={setEmploymentType}
      />
      {err && <div className="text-red-500 text-sm">{err}</div>}
      <div className="flex w-full justify-between">
        <Button
          type="button"
          variant="outline"
          onClick={() => setPage((prev) => prev - 1)}
        >
          Previous
        </Button>
        <Button type="submit" disabled={err.length > 0}>
          <ChevronRight />
        </Button>
      </div>
    </form>
  );
}

export default EmploymentInfoForm;
